import { CANVAS_ENTITY_CLASS_NAME } from '@/lib/CanvasEngine/const'
import type { Vector2d } from '@/lib/CanvasEngine/types'
import type {
  IRect,
  IRenderer,
  IText,
  PointerEventHandler,
} from '@/lib/CanvasEngine/backend/IRenderer'

/**
 * Balloon Head 설정 인터페이스
 */
export interface BalloonHeadConfig {
  color: string
  fillColor: string
  fontColor: string
  text: string
  size: number
  strokeWidth: number
  fontSize: number
  interactionMode: boolean
  isSelected: boolean
}

/**
 * Balloon Head 클래스
 * Rect와 Text로 이루어진 balloon의 머리 부분을 관리
 */
export class BalloonHead {
  public rect: IRect
  public text: IText
  private config: BalloonHeadConfig

  constructor(
    private readonly renderer: IRenderer,
    config: BalloonHeadConfig,
  ) {
    this.config = config
    this.rect = this.createRect()
    this.text = this.createText()
  }

  /**
   * Balloon Rect 생성
   */
  private createRect(): IRect {
    const { size, color, fillColor, strokeWidth, interactionMode, isSelected } = this.config

    return this.renderer.createRect({
      name: 'balloon',
      stroke: color,
      fill: fillColor,
      strokeWidth,
      width: size,
      height: size,
      offsetX: size / 2,
      offsetY: size / 2,
      cornerRadius: 500,
      hitArea: { kind: 'strokeWidth', px: 20 },
      draggable: interactionMode && isSelected,
    })
  }

  /**
   * Balloon Text 생성
   */
  private createText(): IText {
    const { text, fontColor, fontSize } = this.config

    return (
      this.renderer
        .createText({
          text,
          fontSize,
          fill: fontColor,
          align: 'center',
          verticalAlign: 'middle',
          listening: false,
        })
        // 표식 속성(읽는 곳 없음) — 생성 config와 같은 attrs를 남긴다
        .setAttr('className', CANVAS_ENTITY_CLASS_NAME.READONLY)
    )
  }

  /**
   * Balloon 위치 설정
   */
  public setPosition(pos: Vector2d): void {
    this.rect.x(pos.x)
    this.rect.y(pos.y)
  }

  /**
   * Balloon 색상 변경
   */
  public changeColor(color: string): void {
    this.rect.stroke(color)
    this.config.color = color
  }

  /**
   * Balloon Fill 색상 변경
   */
  public changeFillColor(color: string, opacity?: number): void {
    const isEmptyColor =
      !opacity && (color === 'rgba(255, 255, 255, 1)' || color === '#fff' || color === 'white')
    this.rect.fill(!isEmptyColor ? color : 'rgba(255, 255, 255, 0)')
    this.config.fillColor = color
  }

  /**
   * Balloon Font 색상 변경
   */
  public changeFontColor(color: string): void {
    this.text.fill(color)
    this.config.fontColor = color
  }

  /**
   * Balloon Text 변경
   */
  public setText(text: string): void {
    this.text.text(text)
    this.config.text = text
  }

  /**
   * Balloon 원형/사각형 모양 변경
   */
  public setCircle(isCircle: boolean): void {
    this.rect.cornerRadius(isCircle ? 500 : 5)
  }

  /**
   * Balloon Draggable 설정
   */
  public setDraggable(draggable: boolean): void {
    this.rect.draggable(draggable)
  }

  /**
   * Text 위치 업데이트 (Balloon 중심에 맞춤)
   */
  public updateTextPosition(): void {
    const balloonX = this.rect.x()
    const balloonY = this.rect.y()
    const balloonWidth = this.rect.width()

    // balloonSize에 따라 minusOffset 계산
    let minusOffset = 0
    if (balloonWidth <= 31) {
      minusOffset = 1
    } else if (balloonWidth <= 66) {
      minusOffset = 2
    } else if (balloonWidth <= 90) {
      minusOffset = 3
    }

    this.text.x(balloonX)
    this.text.y(balloonY)
    const textSize = this.text.measure()
    this.text.offsetX(textSize.width / 2)
    this.text.offsetY(textSize.height / 2 - minusOffset)
  }

  /**
   * Balloon이 드래그 중인지 확인
   */
  public isDragging(): boolean {
    return this.rect.isDragging()
  }

  /**
   * Balloon 위치 가져오기
   */
  public getPosition(): Vector2d {
    return {
      x: this.rect.x(),
      y: this.rect.y(),
    }
  }

  /**
   * Balloon 크기 가져오기
   */
  public getSize(): number {
    return this.config.size
  }

  /**
   * 이벤트 리스너 등록
   */
  public on(eventName: string, handler: PointerEventHandler): void {
    this.rect.on(eventName, handler)
  }

  /**
   * 레이어 순서 변경
   */
  public moveToTop(): void {
    this.rect.moveToTop()
    this.text.moveToTop()
  }

  /**
   * 절대 좌표로 위치 변환
   */
  public toAbsolutePosition(entityPos: Vector2d): Vector2d {
    return {
      x: entityPos.x + this.rect.x(),
      y: entityPos.y + this.rect.y(),
    }
  }
}
